import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader, { LUXKIT_MODAL_HEADER_TITLE_ID } from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import FlashPackageSelectionActions, { type FlashPackageSelectionActionsProps } from './FlashPackageSelectionActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

const BnblModal = lazy(() => import('components/OfferPage/BnblModal/BnblModal'))

interface Props {
  offer: App.HotelOffer
  actions: FlashPackageSelectionActionsProps
  onClose: () => void
  onBeforePurchase: () => boolean
}

/** One focus trap and sheet for options and all of the existing BNBL/gift steps. */
function FlashPackageSelectionOptionsSheet({ offer, actions, onClose, onBeforePurchase }: Props) {
  const intl = useIntl()
  const [route, setRoute] = useState<'options' | 'bnbl'>('options')
  const [prepared, setPrepared] = useState(false)
  const headingRef = useRef<HTMLSpanElement>(null)
  const openBnbl = useCallback(() => {
    setPrepared(true)
    setRoute('bnbl')
  }, [])
  const backToOptions = useCallback(() => setRoute('options'), [])
  const actionPresentation = useMemo(() => ({ kind: 'embedded' as const, resume: prepared, onPrepared: openBnbl }), [openBnbl, prepared])
  const bnblPresentation = useMemo(() => ({ kind: 'embedded' as const, onRootBack: backToOptions, onBeforePurchase }), [backToOptions, onBeforePurchase])

  useEffect(() => {
    if (route === 'options') headingRef.current?.focus()
  }, [route])

  const optionsHeader = <ModalHeader
    title={<span ref={headingRef} tabIndex={-1}>{intl.formatMessage({ id: 'IReH9MYRCXTjewHbJRJ_', defaultMessage: 'More options', description: 'Title of the Flash hotel package alternate purchase actions sheet' })}</span>}
    titleAppearance="body"
    onBackButtonClick={route === 'bnbl' ? backToOptions : undefined}
    onCloseButtonClick={onClose}
  />

  return <ModalBase height="auto" size="XL" onClose={onClose} aria-labelledby={LUXKIT_MODAL_HEADER_TITLE_ID}>
    <>
      {(route === 'options') && (<>
        {optionsHeader}
        <ModalBody>
          <FlashPackageSelectionActions {...actions} bnblPresentation={actionPresentation} />
        </ModalBody>
      </>)}
      {!(route === 'options') && (<Suspense fallback={<>{optionsHeader}<ModalBody><LoadingIndicator /></ModalBody></>}><BnblModal offer={offer} closeBnblModal={onClose} presentation={bnblPresentation} /></Suspense>)}
    </>
  </ModalBase>
}

export default FlashPackageSelectionOptionsSheet
